// React
import React, {useState, useEffect, useRef, useMemo} from "react";

import ReactDataGrid from "react-data-grid";
import Row from "react-data-grid/lib/Row";
import "react-data-grid/dist/react-data-grid.css";
import { ToolsPanel, Data, Draggable } from "react-data-grid-addons";

import {map as _map, find as _find, reduce as _reduce, isEqual as _isEqual, startCase as _startCase, filter as _filter, pick as _pick, forEach as _forEach, forIn as _forIn, keys as _keys} from "lodash-es";

import { Form, Spinner } from "react-bootstrap";

// Ant design
import { Button } from "antd";
import { FilterOutlined, PlusOutlined } from '@ant-design/icons';

import uuidv4 from "uuid/v4";

// Eventify API
import * as EventifyAPI from "../libs/EventifyAPI";
import * as ModelCache from "../libs/ModelCache";

import {getCurrentUser} from "../libs/UserContext";

import * as Grid from "../libs/Grid"

import ListTableAddPerson from "./ListTableAddPerson";
import ListTableDropdownEditor from "./ListTableDropdownEditor";
import ListTableDropdownFilter from "./ListTableDropdownFilter";
import ListTableRowActions from "./ListTableRowActions";

// Local Storage
import ls from "local-storage";

const DraggableContainer = Draggable.Container;
const Toolbar = ToolsPanel.AdvancedToolbar;
const GroupedColumnsPanel = ToolsPanel.GroupedColumnsPanel;

const ynpOptions = [
  {value: "", text: ""},
  {value: "opened", text: "Opened"},
  {value: "delivered", text: "Delivered"},
  {value: "yes", text: "Yes"},
  {value: "no", text: "No"},
  {value: "pending", text: "Pending"}
]
const ynpDropdownEditor = React.forwardRef((props, ref) => {
  return <ListTableDropdownEditor ref={ref} {...props} options={ynpOptions} />;
});
const ynpFilterRenderer = React.forwardRef((props, ref) => {
  return <div className="header-filter">
    <ListTableDropdownFilter ref={ref} {...props} options={ynpOptions} />
  </div>;
});

const anaOptions = [
  {value: "", text: ""},
  {value: "attended", text: "Attended"},
  {value: "no", text: "No"}
]
const anaDropdownEditor = React.forwardRef((props, ref) => {
  return <ListTableDropdownEditor ref={ref} {...props} options={anaOptions} />;
});
const anaFilterRenderer = React.forwardRef((props, ref) => {
  return <div className="header-filter">
    <ListTableDropdownFilter ref={ref} {...props} options={anaOptions} />
  </div>;
});

const textFilterRenderer = React.forwardRef((props, ref) => {
  return <div className="header-filter">
    <Form.Control
      placeholder="Filter..."
      value={props.value}
      onChange={(e) => props.onChange(e.target.value)}
    />;
  </div>
});

const isEditableFn = (row) => { return !row.isAddRow }

function RowRenderer(props) {
  const newProps = {...props};
  if (props.rowIdx === 0) {
    return <ListTableAddPerson
      rowProps={props}
      listId={props.row.listId}
      inListAlready={props.row.inListAlready}
      addPersonToList={props.row.addPersonToList}
    />;
  } else if (props.row.status === 'addSuggestion') {
    newProps.className = "status-add-suggestion";
  } else if (props.row.status === 'deleteSuggestion') {
    newProps.className = "status-delete-suggestion";
  }
  return <Row {...newProps} />;
}

function ownerFormatter ({row}) {
  return <img
    className="avatar sm"
    src={row.contributor?.picture}
    title={row.contributor?.name}
  />
}

function dropdownFormatter ({row, column}) {
  return _startCase(row[column.key]);
}


const ListTable = React.forwardRef((props, ref) => {
  const [list, _setList] = useState([]);
  // to keep things sync'd, the only thing that should call _setGridList is _setList
  const [gridList, _setGridList] = useState([]);
  const [personIdsHash, setPersonIdsMap] = useState({});
  const [height, setHeight] = useState(0);
  // TODO: enable when rdg canary re-adds range selection support
  // const [selectionBounds, _setSelectionBounds] = useState({});
  const [selectedCell, _setSelectedCell] = useState({});
  const [isAddRowClicked, setIsAddRowClicked] = useState({});
  const [sortDirection, setSortDirection] = useState("DESC");
  const [sortColumn, setSortColumn] = useState("suggestion");
  const [filters, setFilters] = useState({});
  const [enableFilters, setEnableFilters] = useState(false);
  const [groupBy, setGroupBy] = useState(["group"]);
  const groupedRows = Data.Selectors.getRows({ list, groupBy });
  const [teamUsers, _setTeamUsers] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  const currentUser = getCurrentUser();
  const userId =  currentUser.email;
  console.log(userId);
  const isListOwner = userId === props.list.userid;
  console.log(props.list.userid);

  // TODO: look into using dispatches https://reactjs.org/docs/hooks-faq.html#how-to-avoid-passing-callbacks-down
  const listRef = useRef(list);

  React.useImperativeHandle(ref, () => ({
    exportList: () => {
      return _filter(_map(list, (row, i) => {
        console.log(row.membership);
        if ((row.membership?.status?.indexOf("uggest") || -1) !== -1) {
          return;
        }
        const contributor = row.owner || row.adder;
        return {
          status: row.membership.status,
          name: row.name,
          email: row.membership.contactEmail || row.email,
          notes: row.notes?.notes,
          // ynp: row.membership.ynp,
          // ana: row.membership.ana,
          suggestion: getSuggestionString(row.name, row.membership, row.suggestor),
          contributor: row.owner || row.adder
        };
      }));
    },
  }));

  // so document listener can access https://medium.com/geographit/accessing-react-state-in-event-listeners-with-usestate-and-useref-hooks-8cceee73c559
  // const gridListRef = useRef(gridList);
  const setList = (list) => {
    _setList(list);
    listRef.current = list;

    const gridList = _filter(_map(list, (row, i) => {
      if (row.membership.status === "deleted") {
        return;
      }
      const contributor = row.owner || row.adder;
      return {
        status: row.membership.status,
        actions: <ListTableRowActions
          idx={i+1} // +1 to account for top "add" row
          row={row}
          onUpdate={onRowsUpdate}
          isListOwner={isListOwner}
          userId={userId}
          name={row.name}
          email={row.membership.contactEmail || row.email}
        />,
        name: row.name,
        email: row.membership.contactEmail || row.email,
        notes: row.notes?.notes,
        // ynp: row.membership.ynp,
        // ana: row.membership.ana,
        suggestion: getSuggestionString(row.name, row.membership, row.suggestor),
        group: row.membership?.group,
        contributor: row.owner || row.adder
      };
    }));
    // gridListRef.current = gridList;
    _setGridList(gridList);
  }

  const suggestionStringMessagingMap = {
    addSuggestion: "suggests adding",
    deleteSuggestion: "suggests removing"
  }

  function getSuggestionString(name, membership, suggestor) {
    if (suggestor == null || typeof suggestor === "undefined") {
      return "";
    }
    const verb = suggestionStringMessagingMap[membership.status];
    if (!verb) {
      return "";
    }

    const owner = suggestor.firstName || suggestor.name.split(' ')[0];
    return owner + " " + verb;
  }

  const teamUsersRef = useRef(teamUsers);
  const setTeamUsers = (users) => {
    teamUsersRef.current = users;
    _setTeamUsers(users);
  }
  const ownerDropdownEditor = React.forwardRef(({value, ...props}, ref) => {
    const ownerOptions = teamUsersRef.current.map((user) => {
      return {value: user.email, text: user.fullName}
    });
    return <ListTableDropdownEditor ref={ref} value={value.email} {...props} options={ownerOptions} />;
  });


  const selectedCellRef = useRef(selectedCell);
  const setSelectedCell = (cell) => {
    selectedCellRef.current = cell;
    _setSelectedCell(cell);
  }

  // TODO: enable when rdg canary re-adds range selection support
  // so document listener can access https://medium.com/geographit/accessing-react-state-in-event-listeners-with-usestate-and-useref-hooks-8cceee73c559
  // const selectionBoundsRef = useRef(selectionBounds);
  // const setSelectionBounds = (bounds) => {
  //   selectionBoundsRef.current = bounds
  //   _setSelectionBounds(bounds);
  // }

  const containerRef = useRef(null);
  const gridRef = useRef(null);

  useEffect(() => {
    onLoad();

    return onUnload;
  }, []);

  function onLoad() {
    setHeight(containerRef.current.clientHeight);
    // TODO: enable when rdg canary re-adds range selection support
    // document.addEventListener('copy', handleCopy);
    // document.addEventListener('paste', handlePaste);
    ModelCache.getMembershipForListId(
      props.list.listid,
      ( (result) => {
        if (typeof result !== "undefined") {
          console.log(result);
          setList(Grid.sortRows(result, sortColumn, sortDirection));
          setSortDirection(sortDirection);
          setSortColumn(sortColumn);
          setPersonIdsMap(_reduce(result, (obj, row) => {
            obj[row.personid] = true;
            return obj;
          }, {}));
          updateListStatsWithList(result);
        }
      }));
      EventifyAPI.getAllUsers().then( (users) => {
        setTeamUsers(users);
      });
  }

  function onUnload() {
    // TODO: enable when rdg canary re-adds range selection support
    // document.removeEventListener('copy', handleCopy);
    // document.removeEventListener('paste', handlePaste);
  }

  function getListStats(theList) {
    const pendingApprovals = _filter(theList, (row) => {
      return (row.membership?.status || "") === "addSuggestion";
    });
    const pendingRemovals = _filter(theList, (row) => {
      return (row.membership?.status || "") === "deleteSuggestion";
    });
    return {
      headcount: theList.length,
      pendingApprovals: pendingApprovals.length,
      pendingRemovals: pendingRemovals.length
    };
  }

  function updateListStatsWithList(theList) {
    props.setListStats(getListStats(theList));
  }

  const columns = useMemo(() => {
    const stats = getListStats(list);
    return [{
      key: 'actions',
      name: <div
        className="filter"
        title="Show filters"
        onClick={() => {
          setEnableFilters(!enableFilters);
        }}
      >
        <div><FilterOutlined /></div>
        {isSaving &&
          <div className="spinner">
            <Spinner animation="border" size="sm" />
          </div>}
      </div>,
      width: 65
    },
    { key: 'suggestion', name: 'Review', sortable: true, filterRenderer: textFilterRenderer, editable: ()=>{return false}, width: stats.pendingRemovals + stats.pendingApprovals === 0 ? 30 : 200},
    { key: 'name', name: 'Name', sortable: true, filterRenderer: textFilterRenderer, editable: isEditableFn, width: 150, draggable: true },
    { key: 'email', name: 'Email', sortable: true, filterRenderer: textFilterRenderer, editable: isEditableFn, width: 230 },
    { key: 'notes', name: 'Notes', sortable: true, filterRenderer: textFilterRenderer, editable: isEditableFn },
    { key: 'group', name: 'Section', sortable: true, filterRenderer: textFilterRenderer, editable: isEditableFn, draggable: true},
    // TODO: our own dropdown editor with antd?
    //{ key: 'ynp', name: 'YNP', sortable: true, filterRenderer: ynpFilterRenderer, editor: ynpDropdownEditor, formatter: dropdownFormatter, width: 80 },
  //  { key: 'ana', name: 'Attended', sortable: true, filterRenderer: anaFilterRenderer, editor: anaDropdownEditor, formatter: dropdownFormatter, width: 80 },
    { key: 'contributor', name: 'Owner', sortable: true, editor: ownerDropdownEditor, formatter: ownerFormatter, width: 80}
  ]}, [enableFilters, isSaving, list]);

  // TODO: enable when rdg canary re-adds range selection support
  // const handleCopy = (e) => { Grid.handleCopy(e, selectionBoundsRef, columns, gridListRef) }
  // const handlePaste = (e) => { Grid.handlePaste(e, selectionBoundsRef, setList) }

  const rowUpdateToListObj = (current, diff) => {
    diff = _reduce(diff, (newDiff, value, key) => {
      if (key === 'notes') {
        newDiff.notes = {notes: value};
      } else if (key === 'ana' || key === 'ynp') {
        newDiff.membership = { ...current.membership, ...{ [key]: value } };
      } else if (key === 'status') {
        newDiff.membership = { ...current.membership, ...{ status: value, statusUserId: userId } };
        newDiff.suggestor = { ...current.suggestor, ...currentUser }
      } else if (key === 'contributor') {
        newDiff.owner = _find(teamUsers, {email: value});
      } else if (key === 'group') {
        newDiff.membership = { ...current.membership, ...{ group: value } };
      } else {
        newDiff[key] = value;
      }
      return newDiff;
    }, {});
    return { ...current, ...diff }
  }

  const addPersonToList = async (row) => {
    setIsSaving(true);
    // console.log(row.selectedContact);
    let contactId = row.selectedContact.personid != null && typeof row.selectedContact.personid !== "undefined" ? row.selectedContact.personid : uuidv4();
    const status = isListOwner ? 'approved' : 'addSuggestion';
    let newList = [{
      name: row.name,
      email: row.email,
      personid: contactId,
      adder: _pick(currentUser, ['email', 'picture', 'fullName', 'firstName', 'lastName']),
      membership: {
        ana: row.ana,
        ynp: row.ynp,
        status: status,
        userid: userId,
        personid: contactId
      },
      suggestor: _pick(currentUser, ['email', 'picture', 'fullName', 'firstName', 'lastName']),
      notes: {
        notes: row.notes
      }
    }].concat(list);
    setList(newList);
    EventifyAPI.createListMembership({
      personId: contactId,
      listId: row.listId,
      name: row.name,
      email: row.email,
      notes: row.notes,
      status: status,
      contactEmail: row.email
    }).then( async (result) => {
      setIsSaving(false);
      props.refreshHistory();
    });
    setPersonIdsMap(_reduce(newList, (obj, row) => {
      obj[row.personid] = true;
      return obj;
    }, {}));
    updateListStatsWithList(newList);
  }

  const onRowsUpdate = ({fromRow, toRow, updated, action, name, email}) => {
    console.log(fromRow, toRow, updated, action, name, email);
    // account for the "add" row we inject to start of grid list
    fromRow -= 1;
    toRow -= 1;

    const rows = listRef.current.slice();

    const localGridList = _filter(_map(rows, (row, i) => {
      if (row.membership.status === "deleted") {
        return;
      }
      const contributor = row.owner || row.adder;
      return {
        status: row.membership.status,
        actions: null,
        name: row.name,
        email: row.membership.contactEmail || row.email,
        notes: row.notes?.notes,
        // ynp: row.membership.ynp,
        // ana: row.membership.ana,
        suggestion: getSuggestionString(row.name, row.membership, row.suggestor),
        group: row.membership?.group,
        contributor: row.owner || row.adder
      };
    }));

    const originalFromRow = fromRow;

    let onlyRows = [];
    let iter = 0;
    let keepGoing = true;
    let affectedPersonObject = {};
    const localFilteredGridList = calcFilteredGridList(localGridList);
    console.log(localFilteredGridList);
    _forEach(localFilteredGridList, (row) => {
      if (row.__metaData?.isGroup === true) {
        if (keepGoing) {
          fromRow -= 1;
          toRow -= 1;
        }
      } else {
        if (keepGoing === true && action === true) {
          console.log(action, name, email);
          affectedPersonObject = {name: name, email: email};
          keepGoing = false;
        } else if (keepGoing === true && (iter == originalFromRow || iter == rows.length-1)) {
          console.log(row);
          console.log(iter);
          affectedPersonObject = localFilteredGridList[iter];
          keepGoing = false;
        }
        iter++;
      }
    });

    console.log(affectedPersonObject);
    const updates = [];
    iter = 0;
    console.log(rows);
    _forEach(rows, (row) => {
      if (row.name === affectedPersonObject.name && row.email === affectedPersonObject.email) {
        const personId = row.personid;
        console.log(row);
        updates.push({personId, diff: updated});
        rows[iter] = rowUpdateToListObj(row, updated);
      }
      iter++;
    });

    console.log(updates);

    // console.log(rows, filteredGridList, fromRow, toRow);
    // const updates = [];
    // for (let i = fromRow; i <= toRow; i++) {
    //   console.log(rows[i]);
    //   const personId = rows[i].personid;
    //   updates.push({personId, diff: updated});
    //   rows[i] = rowUpdateToListObj(rows[i], updated);
    // }
    updateBackend(updates);

    setList(rows);
    return { rows };
  };

  const updateBackend = (updates) => {
    setIsSaving(true);
    console.log(updates);
    EventifyAPI.updateListMembership(
      props.list.listid, updates
    ).then( async (result) => {
      setIsSaving(false);
      props.refreshHistory();
    });
  }

  // TODO: replace with something better when typeahead is done server-side
  const inListAlready = (personId) => {
    return Boolean(personIdsHash[personId]);
  };

  function calcFilteredGridList(localGridList) {
    const sections = {};
    console.log(list, localGridList);
    _forEach(localGridList, (person) => {
      const groupKey = person.group || "";
      // console.log(person);
      let groupList = sections[groupKey];
      if (groupList == null || typeof groupList === "undefined") {
        groupList = [{name: groupKey + " List", __metaData:{isGroup:true, treeDepth:0, isExpanded: true, columnGroupName: "group", columnGroupDisplayNane: "group"}}];
      }

      groupList.push(person);
      sections[groupKey] = groupList;
    });

    console.log(sections);

    const sortedKeys = _keys(sections).sort((left, right) => {
    if (left === "List") {
      return -1;
    }
    return left > right ? -1 : 1;
    });

    let allGroupLists = [];
    _forIn(sortedKeys, (sectionKey) => {
      const sectionList = sections[sectionKey];
      const filteredGroupList = sectionList.filter(row => {
        return (
          (filters.name ? row.name?.toLowerCase().includes(filters.name.toLowerCase()) : true)
          && (filters.email ? row.email?.toLowerCase().includes(filters.email.toLowerCase()) : true)
          && (filters.notes ? row.notes?.toLowerCase().includes(filters.notes.toLowerCase()) : true)
          && (filters.ynp ? row.ynp === filters.ynp : true)
          && (filters.ana ? row.ana === filters.ana : true)
          && (filters.suggestion ? row.suggestion?.toLowerCase().includes(filters.suggestion.toLowerCase()) : true)
        );
      });
      allGroupLists = allGroupLists.concat(filteredGroupList);
    });

    return allGroupLists;
  }

  const filteredGridList = calcFilteredGridList(gridList);

  let ed = 0;

  const groupColumn = columnKey => {
    const columnGroups = groupBy.slice(0);
    const activeColumn = columns.find(c => c.key === columnKey);
    const isNotInGroups =
      columnGroups.find(c => activeColumn.key === c.name) == null;
    if (isNotInGroups) {
      columnGroups.push({ key: activeColumn.key, name: activeColumn.name });
    }
    console.log(columnGroups);
    return columnGroups;
  };

  const ungroupColumn = columnKey => groupBy => {
    return groupBy.filter(g =>
      typeof g === "string" ? g !== columnKey : g.key !== columnKey
    );
  };

  const CustomToolbar = ({
    groupBy,
    onColumnGroupAdded,
    onColumnGroupDeleted
  }) => {
    return (
      <Toolbar>
        <GroupedColumnsPanel
          groupBy={groupBy}
          onColumnGroupAdded={onColumnGroupAdded}
          onColumnGroupDeleted={onColumnGroupDeleted}
        />
      </Toolbar>
    );
  };

  console.log(groupedRows);

  return (
    <div className="list-table" ref={containerRef}>
      <DraggableContainer>
        <ReactDataGrid
          ref={gridRef}
          columns={columns}
          rows={[{
            isAddRow: true,
            listId: props.list.listid,
            inListAlready: inListAlready,
            addPersonToList: addPersonToList
          }].concat(filteredGridList)}
          rowGetter={i => groupedRows[i]}
          rowsCount={groupedRows.length + 1}
          height={height}
          enableCellSelect={true}
          enableCellAutoFocus={false}
          enableDragAndDrop={true}
          rowHeight={39}
          rowRenderer={RowRenderer}
          onRowsUpdate={onRowsUpdate}
          onSort={(sortColumn, sortDirection) => {
            setList(Grid.sortRows(list, sortColumn, sortDirection));
            setSortDirection(sortDirection);
            setSortColumn(sortColumn);
          }}
          sortDirection={sortDirection}
          sortColumn={sortColumn}
          // TODO: enable when rdg canary re-adds range selection support
          // onSelectedCellRangeChange={(args) => {
          //   setSelectionBounds(Grid.getSelectionBounds(args))
          // }}
          // onSelectedCellChange={(args) => {
          //   const { rowIdx, idx } = args;
          //   // open cell editor on single click
          //   if (!_isEqual(selectedCellRef.current, { rowIdx, idx })) {
          //     setSelectedCell({ rowIdx, idx });
          //     gridRef && gridRef.current.openCellEditor(rowIdx, idx);
          //   }
          // }}
          enableFilters={enableFilters}
          filters={filters}
          onFiltersChange={setFilters}
        />
      </DraggableContainer>
    </div>
  )
});

export default ListTable;
